import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import fetchWrapper from '../utils/httpClient';
import { API_GET_PROMO_CODES, API_EXPORT_PROMO_CODES } from '../utils/apis';

const initialState = {
  promoCodes: { resdata: { content: [] } },
  currentCampaign: null,
  loading: false,
  exportLoading: false,
  error: null,
  updateStatus: null,
  createStatus: null,
  deleteStatus: null,
  exportStatus: null,
  exportData: null,
  queryParams: {},
  pagination: {
    pageNumber: 0,
    totalRecords: 0,
    totalPages: 0,
    pageSize: 10
  }
};

export const fetchPromoCodes = createAsyncThunk(
  'manageCampaign/fetchPromoCodes',
  async (queryParams = {}) => {
    const params = new URLSearchParams();

    if (queryParams.page !== undefined) params.append('page', queryParams.page);
    if (queryParams.size !== undefined) params.append('size', queryParams.size);

    const url = params.toString()
      ? `${API_GET_PROMO_CODES}?${params.toString()}`
      : API_GET_PROMO_CODES;

    const data = await fetchWrapper.get(url);
    return data;
  }
);

export const fetchCampaignById = createAsyncThunk(
  'manageCampaign/fetchCampaignById',
  async (uuid) => {
    const data = await fetchWrapper.get(`${API_GET_PROMO_CODES}/${uuid}`);
    return data;
  }
);

export const updateCampaign = createAsyncThunk(
  'manageCampaign/updateCampaign',
  async ({ uuid, campaignData }) => {
    const data = await fetchWrapper.put(`${API_GET_PROMO_CODES}/${uuid}`, campaignData);
    return data;
  }
);

export const createCampaign = createAsyncThunk(
  'manageCampaign/createCampaign',
  async (campaignData) => {
    const data = await fetchWrapper.post(API_GET_PROMO_CODES, campaignData);
    return data;
  }
);

export const deleteCampaign = createAsyncThunk(
  'manageCampaign/deleteCampaign',
  async (uuid) => {
    await fetchWrapper.delete(`${API_GET_PROMO_CODES}/${uuid}`);
    return uuid;
  }
);

export const exportPromoCodes = createAsyncThunk(
  'manageCampaign/exportPromoCodes',
  async (exportParams = {}) => {
    const blob = await fetchWrapper.getFileWithPost(API_EXPORT_PROMO_CODES, exportParams, {
      headers: {
        'Accept': 'text/csv'
      }
    });
    return blob;
  }
);

const manageCampaignSlice = createSlice({
  name: 'manageCampaign',
  initialState,
  reducers: {
    resetCurrentCampaign(state) {
      state.currentCampaign = null;
    },
    resetUpdateStatus(state) {
      state.updateStatus = null;
    },
    resetCreateStatus(state) {
      state.createStatus = null;
    },
    resetDeleteStatus(state) {
      state.deleteStatus = null;
    },
    resetExportStatus(state) {
      state.exportStatus = null;
      state.exportData = null;
    },
    setQueryParams(state, action) {
      state.queryParams = { ...state.queryParams, ...action.payload };
    },
    setPagination(state, action) {
      state.pagination = { ...state.pagination, ...action.payload };
    },
    downloadExportedFile(state) {
      if (state.exportData) {
        const url = window.URL.createObjectURL(state.exportData);
        const link = document.createElement('a');
        link.href = url;

        link.setAttribute('download', `promo-codes-${new Date().toISOString().split('T')[0]}.csv`);
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
      }
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchPromoCodes.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchPromoCodes.fulfilled, (state, action) => {
        state.promoCodes = action.payload;
        state.loading = false;

        if (action.payload.resdata) {
          state.pagination = {
            pageNumber: action.payload.resdata.number || 0,
            totalRecords: action.payload.resdata.totalElements || action.payload.resdata.content.length,
            totalPages: action.payload.resdata.totalPages || 1,
            pageSize: action.payload.resdata.size || 10
          };
        }
      })
      .addCase(fetchPromoCodes.rejected, (state, action) => {
        state.error = action.error.message;
        state.loading = false;
      });

    builder
      .addCase(fetchCampaignById.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchCampaignById.fulfilled, (state, action) => {
        state.currentCampaign = action.payload;
        state.loading = false;
      })
      .addCase(fetchCampaignById.rejected, (state, action) => {
        state.error = action.error.message;
        state.loading = false;
      });

    builder
      .addCase(updateCampaign.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.updateStatus = 'pending';
      })
      .addCase(updateCampaign.fulfilled, (state, action) => {
        state.loading = false;
        state.updateStatus = 'success';
        state.currentCampaign = action.payload;
      })
      .addCase(updateCampaign.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message;
        state.updateStatus = 'failed';
      });

    builder
      .addCase(createCampaign.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.createStatus = 'pending';
      })
      .addCase(createCampaign.fulfilled, (state, action) => {
        state.loading = false;
        state.createStatus = 'success';
        state.currentCampaign = action.payload;
      })
      .addCase(createCampaign.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message;
        state.createStatus = 'failed';
      });

    builder
      .addCase(deleteCampaign.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.deleteStatus = 'pending';
      })
      .addCase(deleteCampaign.fulfilled, (state, action) => {
        state.loading = false;
        state.deleteStatus = 'success';
        state.promoCodes.resdata.content = state.promoCodes.resdata.content.filter(
          item => item.uuid !== action.payload
        );
        if (state.currentCampaign && state.currentCampaign.uuid === action.payload) {
          state.currentCampaign = null;
        }
      })
      .addCase(deleteCampaign.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message;
        state.deleteStatus = 'failed';
      });

    builder
      .addCase(exportPromoCodes.pending, (state) => {
        state.exportLoading = true;
        state.error = null;
        state.exportStatus = 'pending';
      })
      .addCase(exportPromoCodes.fulfilled, (state, action) => {
        state.exportLoading = false;
        state.exportStatus = 'success';
        state.exportData = action.payload;
      })
      .addCase(exportPromoCodes.rejected, (state, action) => {
        state.exportLoading = false;
        state.error = action.error.message;
        state.exportStatus = 'failed';
      });
  }
});

export const {
  resetCurrentCampaign,
  resetUpdateStatus,
  resetCreateStatus,
  resetDeleteStatus,
  resetExportStatus,
  downloadExportedFile
} = manageCampaignSlice.actions;

export default manageCampaignSlice.reducer;